import { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import api from "../../api/client";
import { AuthContext } from "../../auth/AuthContext";

const PortalDataContext = createContext();

export function PortalDataProvider({ children }) {
  const { token, orgId } = useContext(AuthContext);

  const [prescriptions,          setPrescriptions]          = useState([]);
  const [uploads,                setUploads]                = useState([]);
  const [unreadNotifCount,       setUnreadNotifCount]       = useState(0);
  const [refillRequests,         setRefillRequests]         = useState([]);
  const [pharmacies,             setPharmacies]             = useState([]);
  const [myDepartments,          setMyDepartments]          = useState([]);
  const [allDepartments,         setAllDepartments]         = useState([]);
  const [loading,                setLoading]                = useState(true);
  const [selectedPharmacyName,   setSelectedPharmacyName]   = useState(null);
  const [selectedDepartmentName, setSelectedDepartmentName] = useState(null);

  const deptFetchedForOrg = useRef(null);

  // FIX 3: keep a ref to the latest orgId so WS onmessage never uses a
  // stale closure value even after orgId changes
  const orgIdRef = useRef(orgId);
  useEffect(() => { orgIdRef.current = orgId; }, [orgId]);

  // ── Fetch pharmacies only — no org needed ─────────────────────────────
  const fetchPharmacies = useCallback(() => {
    if (!token) return;
    api.get("pharmacies/")
      .then(res => setPharmacies(res.data))
      .catch(err => console.error("Pharmacies fetch error", err));
  }, [token]);

  // ── Fetch org-specific data — requires orgId ──────────────────────────
  // FIX 4: pass currentOrgId explicitly as a header so requests never go
  // out without X-Organization-Id regardless of localStorage timing
  const fetchOrgData = useCallback((currentOrgId) => {
    if (!token || !currentOrgId) return;
    setLoading(true);
    const headers = { "X-Organization-Id": currentOrgId };
    Promise.all([
      api.get("prescriptions/",          { headers }),
      api.get("refill-requests/",        { headers }),
      api.get("prescription-uploads/",   { headers }),
      api.get("portal-notifications/?page=1&page_size=1", { headers }),
    ])
      .then(([rxRes, refillRes, uploadsRes, notifRes]) => {
        const rxData = rxRes.data;
        setPrescriptions(Array.isArray(rxData) ? rxData : (rxData.prescriptions ?? []));
        setRefillRequests(refillRes.data);
        setUploads(Array.isArray(uploadsRes.data) ? uploadsRes.data : []);
        setUnreadNotifCount(notifRes.data.unread_count ?? 0);
      })
      .catch(err => console.error("OrgData fetch error", err))
      .finally(() => setLoading(false));
  }, [token]);

  // ── Fetch departments — once per orgId ───────────────────────────────
  const fetchDepartments = useCallback((currentOrgId) => {
    if (!token || !currentOrgId) return;
    if (deptFetchedForOrg.current === currentOrgId) return;

    const headers = { "X-Organization-Id": currentOrgId };
    Promise.all([
      api.get("my-departments/",  { headers }),
      api.get("all-departments/", { headers }),
    ])
      .then(([myRes, allRes]) => {
        setMyDepartments(myRes.data);
        setAllDepartments(allRes.data);
        deptFetchedForOrg.current = currentOrgId;
      })
      .catch(err => console.error("Department fetch error", err));
  }, [token]);

  // ── On token change — fetch pharmacies; org data only if orgId exists ─
  // FIX 5: use a ref to mark that this effect already triggered org fetches
  // so the [orgId] effect below doesn't duplicate them on initial mount
  const initialOrgFetchDone = useRef(false);

  useEffect(() => {
    initialOrgFetchDone.current = false; // reset on every token change

    if (!token) {
      setPrescriptions([]);
      setRefillRequests([]);
      setUploads([]);
      setUnreadNotifCount(0);
      setPharmacies([]);
      setMyDepartments([]);
      setAllDepartments([]);
      deptFetchedForOrg.current = null;
      setLoading(false);
      return;
    }

    fetchPharmacies();

    if (orgId) {
      fetchOrgData(orgId);
      fetchDepartments(orgId);
      initialOrgFetchDone.current = true;
    } else {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  // intentionally omit orgId — this effect is only for token changes;
  // orgId changes are handled by the effect below

  // ── WebSocket ─────────────────────────────────────────────────────────
  const wsRef = useRef(null);

  useEffect(() => {
    if (!token || !orgId) return;

    const base = (process.env.REACT_APP_BASE_URL || "http://localhost:8000/portal/")
      .replace(/\/portal\/?$/, "")
      .replace("https://", "wss://")
      .replace("http://", "ws://");

    let ws;
    let reconnectTimer;
    let destroyed = false;

    const connect = () => {
      ws = new WebSocket(`${base}/ws/org/${orgId}/`);
      wsRef.current = ws;

      // FIX 1: use fetchOrgData/fetchDepartments (not the old fetchCoreData)
      // FIX 3: read orgIdRef.current so callback always has the latest orgId
      ws.onmessage = (e) => {
        const data = JSON.parse(e.data);
        if (data.type === "notification" && data.target === "portal") {
          setUnreadNotifCount(prev => prev + 1);
        }
        if (data.type === "data_changed") {
          fetchOrgData(orgIdRef.current);
          if (data.entity === "departments") {
            deptFetchedForOrg.current = null; // force dept re-fetch
            fetchDepartments(orgIdRef.current);
          }
        }
      };

      // FIX 2: reconnect on unexpected close
      ws.onclose = () => {
        if (!destroyed) {
          reconnectTimer = setTimeout(connect, 3000);
        }
      };
    };

    connect();

    return () => {
      destroyed = true;
      clearTimeout(reconnectTimer);
      ws?.close();
    };
  }, [token, orgId]); // reconnects automatically when orgId changes (pharmacy switch)

  // ── On orgId change — fetch org-specific data and departments ─────────
  useEffect(() => {
    if (!orgId || !token) return;

    // FIX 5: skip if the [token] effect already ran these fetches on mount
    if (!initialOrgFetchDone.current) {
      fetchOrgData(orgId);
      fetchDepartments(orgId);
    }
    // Always clear the flag after first visit so subsequent orgId changes
    // (pharmacy switching) always trigger a full re-fetch
    initialOrgFetchDone.current = false;
  }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived stats ─────────────────────────────────────────────────────
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const activePrescriptionCount = prescriptions.filter(rx =>
    !rx.next_refill || new Date(rx.next_refill) >= today
  ).length;

  const pendingRefillCount = refillRequests.filter(r => r.status === "pending").length;
  const recentRefills      = refillRequests.slice(0, 5);

  const refreshRefills = useCallback(() => {
    if (!token || !orgIdRef.current) return;
    setLoading(true);
    api.get("refill-requests/", { headers: { "X-Organization-Id": orgIdRef.current } })
      .then(res => setRefillRequests(res.data))
      .catch(err => console.error("Refill requests fetch error", err))
      .finally(() => setLoading(false));
  }, [token]);

  const refresh = useCallback(() => {
    deptFetchedForOrg.current = null;
    fetchPharmacies();
    if (orgIdRef.current) {
      fetchOrgData(orgIdRef.current);
      fetchDepartments(orgIdRef.current);
    }
  }, [fetchPharmacies, fetchOrgData, fetchDepartments]);

  return (
    <PortalDataContext.Provider value={{
      activePrescriptionCount,
      pendingRefillCount,
      totalPrescriptionCount:  prescriptions.length,
      pharmacyCount:           pharmacies.length,
      prescriptions,
      refillRequests,
      uploads,
      unreadNotifCount,
      setUnreadNotifCount,
      pharmacies,
      myDepartments,
      allDepartments,
      selectedPharmacyName,
      selectedDepartmentName,
      setSelectedPharmacyName,
      setSelectedDepartmentName,
      recentRefills,
      loading,
      refresh,
      refreshRefills,
    }}>
      {children}
    </PortalDataContext.Provider>
  );
}

export function usePortalData() {
  return useContext(PortalDataContext);
}